<template>
  <div class="question-img-btn" id="question-image-v2-btn">
    <!-- question img btn -->
    <div
      class="input-group-prepend pointer"
      @click="_openModalUploadImage"
    >
      <!-- Question image -->
      <div
        class="input-group-text horizontal layout center center-justified question-img-btn-box question-img-v2"
        :class="{ 'btn-box-edit': questionImage }"
      >
        <img :src="questionImage" alt v-if="questionImage && !isUploading" />
        <i class="far fa-image" v-if="!questionImage && !isUploading" />
        <i class="fas fa-pencil-alt" ref="question-img-edit-btn" v-if="!isUploading" />
        <span class="loading-spinner black d-block" v-if="isUploading" />
      </div>
    </div>

    <!-- question img tooltip to change/delete img -->
    <div
      class="question-img-tooltip"
      v-if="isQuestionImageTooltipVisible"
      v-click-outside="closeEditDeleteQuestionImage"
    >
      <div class="tooltip-box vertical layout center center-justified">
        <aha-button
          class="mb-2"
          size="sm"
          id="question-image-v2-edit-btn"
          @click="handleEditImageQuestionButtonClick()"
        >
          {{ $i18nPresenter.t('Edit') }}
        </aha-button>
        <aha-button
          variant="danger"
          size="sm"
          id="question-image-v2-delete-btn"
          @click="isDeleteQuestionImageModalVisible = true"
        >
          {{ $i18nPresenter.t('Delete') }}
        </aha-button>
      </div>
      <div class="tooltip-arrow" />
    </div>
    <!-- question img upload modal -->
    <UploadImageV2
      v-if="isShowUploadModal"
      @hideModal="_hideModalUploadImage"
      :type="'slideImage'"
      @callback="handleUploadImageForm"
      :updateGifInBackground="handleUpdateGifInBackground"
      @selectExistImage="selectExistImage"
    />

    <CropImageV2
      :image-url="imageUrl"
      :cropper-data="cropperData"
      :aspect-ratio="aspectRatio"
      @hideModal="_closeModalCrop"
      v-if="isCropImageModalShowing"
      @on-cancel="handleCancelCrop"
      :updateInBackground="handleUpdateInBackground"
    />

    <ModalDeleteQuestionImage
      @hide="isDeleteQuestionImageModalVisible = false"
      v-if="isDeleteQuestionImageModalVisible"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImageV2 from '@/components/presentations/CropImage.v2.vue'
import ModalDeleteQuestionImage from '@/components-v2/modals/DeleteQuestionImage.vue'
import BackgroundUploadMixin from '@/mixins/slide/BackgroundUploadMixin'

import Config from '@/config'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import SlideApi from '@/api/slide'
import {
  setObjectToIndexedDB,
} from '@/utils/indexedDB/indexed.db'

export default {
  components: {
    UploadImageV2,
    CropImageV2,
    ModalDeleteQuestionImage,
  },
  mixins: [BackgroundUploadMixin],
  data() {
    return {
      dataConfirm: {},
      isDeleteQuestionImageModalVisible: false,
      isQuestionImageTooltipVisible: false,
      isUploading: false,
      isSlideOptionEditImageTooltipVisible: null,
      isCropImageModalShowing: false,
      imageUrl: '',
      selectedImageOption: {},
      isLoadingOpenOptionImage: false,
      aspectRatio: null,
      isShowUploadModal: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),

    questionImage() {
      return this.slideActive.image && this.slideActive.image.thumbnail
    },
  },
  async mounted() {
    EventBus.$on('open-add-question-image-modal', this._openModalUploadImage)
    EventBus.$on('open-edit-question-image-modal', this.handleEditImageQuestionButtonClick)
    EventBus.$on('open-delete-question-image-modal', this.openDeleteModal)
    await this.checkUnfinishedUploads(this.slideActive.id, 'question_images')
  },
  destroyed() {
    EventBus.$off('open-add-question-image-modal', this._openModalUploadImage)
    EventBus.$off('open-edit-question-image-modal', this.handleEditImageQuestionButtonClick)
    EventBus.$off('open-delete-question-image-modal', this.openDeleteModal)
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    ...mapGetters(['isPresenting', 'getSlideOptionsActive']),

    async selectExistImage(obj) {
      const payload = {
        showQuestionImage: true,
        image: obj.name,
      }

      // api update
      try {
        this.updateSlideActiveAction({
          image: obj,
          showQuestionImage: true,
        })
        await this.$http.put(Config.api.updateSlide + this.slideActive.id, payload)
        EventBus.$emit('eventHandleSetValueTransition')
      } finally {
        this.isShowUploadModal = false
        EventBus.$emit('selectedMyPhotoItem')
      }
    },

    _openModalUploadImage() {
      // set the aspectRatio to NaN which is the defaut value
      // of cropper for aspect ratio
      this.aspectRatio = NaN
      if (!this.questionImage) {
        this.isShowUploadModal = true
      } else {
        this.isQuestionImageTooltipVisible = true
      }
    },

    handleEditImageQuestionButtonClick() {
      const {
        image,
        originQuestionImage,
        questionImageCropperData,
        questionImageExtension,
      } = this.slideActive
      const extension = questionImageExtension || (image.name.split('.').pop())
      if (extension === 'gif') {
        this.isShowUploadModal = true
      } else {
        this.imageUrl = originQuestionImage || image.large
        this.isCropImageModalShowing = true
        this.cropperData = questionImageCropperData
      }
    },

    closeEditDeleteQuestionImage() {
      this.isQuestionImageTooltipVisible = false
    },

    openDeleteModal() {
      this.isDeleteQuestionImageModalVisible = true
    },

    _hideModalUploadImage() {
      this.isShowUploadModal = false
    },

    async handleUploadImageForm(imageUrl) {
      const [originImageUrl] = imageUrl.split('?')
      const extension = originImageUrl.split('.').pop()
      // Skip the gif image, because it will be handled in updateGifBackground()
      if (extension !== 'gif') {
        this.imageUrl = imageUrl
        this.cropperData = null
        this.isShowUploadModal = false
        this.isCropImageModalShowing = true
      }
    },

    _closeModalCrop() {
      this.isCropImageModalShowing = false
    },

    handleCancelCrop() {
      this.isCropImageModalShowing = false
      this.isShowUploadModal = true
    },

    updateQuestionImageLocally({
      externalUrl, blob, cropperData, extension, originQuestionImage, currentSlide,
    }) {
      const url = externalUrl || URL.createObjectURL(blob)
      this.updateSlideActiveAction({
        image: { thumbnail: url, large: url, xLarge: url },
        questionImageExtension: extension,
        questionImageCropperData: cropperData,
        originQuestionImage,
        showQuestionImage: !currentSlide.multipleCountAnswer,
      })

      const payload = {
        blob,
        questionImageCropperData: cropperData,
        originQuestionImage,
        extension,
      }
      setObjectToIndexedDB(currentSlide.id, 'question_images', payload)
      EventBus.$emit('eventHandleSetValueTransition')
    },

    createUploadFormData(data) {
      const formData = new FormData()

      formData.append('file', data.file)
      formData.append('socketId', Socket.getSocketId())
      formData.append('accessCode', data.accessCode)

      return formData
    },

    handleUploadCroppedImage(formData) {
      return this.$http.post(Config.api.uploadImage, formData)
    },

    async handleResizeImage(currentSlide, imageUrl) {
      const croppedImage = imageUrl.split('?')[0]
      const extension = croppedImage.split('.').pop()
      return this.$http.post(Config.api.uploadSlideImage, {
        extension,
        slideId: currentSlide.id,
        image: imageUrl,
        socketId: Socket.getSocketId(),
        accessCode: this.presentation.uniqueAccessCode,
      })
    },

    async handleUploadLinkImage(imageUrl) {
      return this.$http.post(Config.api.uploadLink, {
        image: imageUrl,
      })
    },

    async handleUpdateGifInBackground(data) {
      const { imageUrl, selectedSlides } = data
      const currentSlide = selectedSlides[0]
      const blob = await fetch(imageUrl).then((r) => r.blob())
      try {
        this.updateQuestionImageLocally({
          externalUrl: imageUrl,
          blob,
          cropperData: null,
          extension: 'gif',
          originQuestionImage: null,
          currentSlide,
        })
        this.isShowUploadModal = false
      } catch (e) {
        // do nothing
      } finally {
        const uploadLinkResponse = await this.handleUploadLinkImage(imageUrl)
        // resize
        const s3ImageUrl = uploadLinkResponse.body.file
        const resizeResponse = await this.handleResizeImage(currentSlide, s3ImageUrl)

        // update image url to slide object
        const payload = {
          image: resizeResponse.body,
          originBackgroundImage: null,
          backgroundImageCropperData: null,
          showQuestionImage: !currentSlide.multipleCountAnswer,
        }

        // Update on API
        await SlideApi.patchSlideById(currentSlide.id, payload)
        this.isShowUploadModal = false
      }
    },

    async handleUpdateInBackground(data, unfinishedSlideId) {
      const currentSlide = data.selectedSlides[0]
      if (unfinishedSlideId) {
        await this.increaseRetryTime(unfinishedSlideId, 'question_images')
      } else {
        await this.addUploadTask(currentSlide.id, this.presentation.id, data, 'question_images')
      }

      try {
        this.updateQuestionImageLocally({
          blob: data.blob,
          cropperData: data.cropperData,
          extension: 'jpg',
          originQuestionImage: data.originImageUrl,
          currentSlide,
        })
        this._closeModalCrop()
      } catch (e) {
        // do nothing
      } finally {
        const uploadFormData = this.createUploadFormData(data)

        const uploadRes = await this.handleUploadCroppedImage(uploadFormData)

        const imageUrl = uploadRes.body.file.location
        const resizeRes = await this.handleResizeImage(currentSlide, imageUrl)

        const payload = {
          image: resizeRes.body,
          originQuestionImage: data.originImageUrl,
          questionImageCropperData: data.cropperData,
          showQuestionImage: !currentSlide.multipleCountAnswer,
        }
        // Update slide model on API
        await SlideApi.patchSlideById(currentSlide.id, payload)
        if (unfinishedSlideId) {
          await this.removeUploadTask(unfinishedSlideId, 'question_images')
        } else {
          await this.removeUploadTask(currentSlide.id, 'question_images')
        }
        this._closeModalCrop()
      }
    },

    // async handleFinishCropQuestionImage(data) {
    //   const { slideActive } = this.$store.state
    //   const { id: activeSlideId } = slideActive

    //   // because the croppedImage is signed
    //   // therefore, we need to remove all the queries in the url
    //   const croppedImage = data.crop.split('?')[0]
    //   const extension = croppedImage.split('.').pop()

    //   const res = await this.$http.post(Config.api.uploadSlideImage, {
    //     extension,
    //     image: data.crop,
    //     slideId: activeSlideId,
    //     socketId: Socket.getSocketId(),
    //     accessCode: this.presentation.uniqueAccessCode,
    //   })

    //   this.updateSlideActiveAction({
    //     image: res.body,
    //   })

    //   const payload = {
    //     questionImageCropperData: null,
    //     originQuestionImage: null,
    //     showQuestionImage: !slideActive.multipleCountAnswer,
    //   }

    //   this.updateSlideActiveAction({
    //     showQuestionImage: payload.showQuestionImage,
    //     questionImageCropperData: null,
    //     originQuestionImage: null,
    //   })

    //   await this.$http.put(Config.api.updateSlide + activeSlideId, payload)

    //   this.isCropImageModalShowing = false

    //   EventBus.$emit('eventHandleSetValueTransition')

    //   if (data.originImageUrl) {
    //     const originalImageUrlRes = await this.$http.post(Config.api.uploadLink, {
    //       image: data.originImageUrl,
    //     })

    //     await SlideApi.patchSlideById(activeSlideId, {
    //       questionImageCropperData: data.cropperData,
    //       originQuestionImage: originalImageUrlRes.body.file,
    //     })

    //     if (activeSlideId === this.slideActive.id) {
    //       this.updateSlideActiveAction({
    //         questionImageCropperData: data.cropperData,
    //         originQuestionImage: originalImageUrlRes.body.file,
    //       })
    //     }
    //   }
    // },
  },
}
</script>

<style lang="scss" scoped>
.question-img-btn {
  .question-img-btn-box {
    padding: 14px 12px;
    height: 44px;
    border-radius: 0;
    .fa-image {
      font-size: 17px;
    }
    img {
      max-width: 200%;
      max-height: 30px;
    }
    .fa-pencil-alt {
      display: none;
    }
  }
  .btn-box-edit:hover {
    img {
      display: none;
    }
    .fa-pencil-alt {
      display: block;
    }
  }
}

.question-img-tooltip {
  position: absolute;
  right: 55px;
  top: -17px;
  z-index: 9;
  .tooltip-box {
    background-color: rgba(0, 0, 0, 0.9);
    height: 84px;
    width: 84px;
    border-radius: 5px;
    .btn {
      padding: 0.1rem 0.55rem;
      font-weight: bold;
    }
  }
  .tooltip-arrow {
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid rgba(0, 0, 0, 0.9);
    position: absolute;
    left: 84px;
    top: 40%;
  }
}
</style>
